import {Box, Loader, Center} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import DOMPurify from 'dompurify';
import React, {useEffect} from 'react';

import {generateReactionImage} from 'chemistry/ChemistryDAO';
import {ChemicalStructureDrawerValue} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import InlineBanner, {InlineBannerType, InlineBannerActionType} from 'ui-library/banners/InlineBanner';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import useResizeSVG from 'ui-library/hooks/useResizeSVG';

interface MarvinReactionData {
  base64SVG: string;
  marvinStructureContent: string;
}

async function fetchMarvinContentAndPreview(
  externalFileId: string,
  externalFileUrl: string,
  entryId: string
): Promise<MarvinReactionData | null> {
  const signedUrl = await performSignedFileQuery(externalFileId, externalFileUrl, entryId);
  if (signedUrl != null) {
    const response = await fetch(signedUrl);
    if (response.ok) {
      // Note: could port this to use chemicalStructureFromFile
      const marvinStructureContent = await response.text();
      const {base64SVG} = await generateReactionImage(marvinStructureContent);
      return {base64SVG, marvinStructureContent};
    } else {
      throw new Error('Failed to fetch Marvin file content');
    }
  }
  return null;
}

export default function ReactionSchemeContent({
  externalFileId,
  externalFileUrl,
  entryId,
  openSketcherModalWithStructure,
}: {
  externalFileId: string;
  externalFileUrl: string;
  entryId: string;
  openSketcherModalWithStructure: (marvinStructureContent: ChemicalStructureDrawerValue) => void;
}): JSX.Element {
  const reactionSVGRef = useResizeSVG<HTMLDivElement>(css(styles.reactionSVG));
  const {currentState: marvinContentAndPreviewAsyncState, makeCall: fetchMarvinContentAndPreviewAsync} =
    useAsyncState({makeCall: fetchMarvinContentAndPreview});

  useEffect(() => {
    fetchMarvinContentAndPreviewAsync(externalFileId, externalFileUrl, entryId);
  }, [externalFileId, externalFileUrl, entryId, fetchMarvinContentAndPreviewAsync]);

  const marvinContent =
    marvinContentAndPreviewAsyncState.status === AsyncStatus.SUCCESS &&
    marvinContentAndPreviewAsyncState.data != null
      ? marvinContentAndPreviewAsyncState.data
      : null;

  if (marvinContentAndPreviewAsyncState.status === AsyncStatus.LOADING) {
    return (
      <Center h="100%">
        <Loader size="xl" />
      </Center>
    );
  } else if (marvinContent != null) {
    return (
      <Box
        ref={reactionSVGRef}
        onClick={(e) => {
          // Avoid triggering outer click handler.
          e.stopPropagation();
          openSketcherModalWithStructure(marvinContent.marvinStructureContent);
        }}
        role="img"
        aria-label="Reaction diagram"
        w="100%"
        h="100%"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        // oxlint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(atob(marvinContent.base64SVG)),
        }}
      />
    );
  } else {
    // TODO: Use design-approved error state UI
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Failed to load reaction scheme"
        action={{
          actionType: InlineBannerActionType.CUSTOM,
          actionText: 'Refresh',
          rightAlign: true,
          actionOnClick: () => {
            fetchMarvinContentAndPreviewAsync(externalFileId, externalFileUrl, entryId);
          },
        }}
      />
    );
  }
}

const styles = StyleSheet.create({
  // Ensure SVG scales proportionally within the container. useResizeSVG expects a single className arg so
  // define this style but prefer Mantine props otherwise.
  reactionSVG: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
});
